// modalQueueActions.js
'use strict';
import { SET_OPERATION_DATA, REMOVE_OPERATION_DATA } from '../ActionTypes.js';

import toggleModal from '../action/toggleModal.js';
import getOperationData from '../selector/getOperationData.js';

/**
 * Enqueue a modal action
 * @kind action
 * @param {object} params - action params.
 * @param {string} params.modalId - modal id.
 * @param {object} params.payload - payload.
 * @param {boolean} params.isOpen - is open modal.
 * @param {object} params.modalOption - modal options (can include closeAfterSeconds).
 * @return {Promise} Action promise.
 */
const enqueueModalAction =
  ({ modalId, payload, isOpen, modalOption } = {}) =>
  async (dispatch, getState) => {
    if (!modalId) {
      return dispatch({ type: '' });
    }

    const queue =
      getOperationData(getState(), ['modalQueue', modalId], 'queue') || [];
    const isProcessing =
      getOperationData(getState(), ['modalQueue', modalId], 'isProcessing') ||
      false;

    // Create the action item
    const actionItem = { payload, isOpen, modalOption };

    // Add to queue
    const newQueue = [...queue, actionItem];

    // Update queue in store
    dispatch({
      type: SET_OPERATION_DATA,
      payload: {
        selectPath: ['modalQueue', modalId, 'queue'],
        data: newQueue,
      },
    });

    // If not currently processing, start processing
    if (!isProcessing) {
      return dispatch(processModalQueue({ modalId }));
    }
    return dispatch({ type: '' });
  };

/**
 * Process the modal queue
 * @kind action
 * @param {object} params - action params.
 * @param {string} params.modalId - modal id.
 * @return {Promise} Action promise.
 */
const processModalQueue =
  ({ modalId } = {}) =>
  async (dispatch, getState) => {
    const queue =
      getOperationData(getState(), ['modalQueue', modalId], 'queue') || [];
    const isProcessing =
      getOperationData(getState(), ['modalQueue', modalId], 'isProcessing') ||
      false;

    // If queue is empty, mark as not processing and return
    if (queue.length === 0) {
      return dispatch({
        type: SET_OPERATION_DATA,
        payload: {
          selectPath: ['modalQueue', modalId, 'isProcessing'],
          data: false,
        },
      });
    }

    // If already processing, return (will be called again when current action completes)
    if (isProcessing) {
      return dispatch({ type: '' });
    }

    // Set processing to true
    dispatch({
      type: SET_OPERATION_DATA,
      payload: {
        selectPath: ['modalQueue', modalId, 'isProcessing'],
        data: true,
      },
    });

    // Get and remove first action from queue
    const [currentAction, ...remainingQueue] = queue;
    dispatch({
      type: SET_OPERATION_DATA,
      payload: {
        selectPath: ['modalQueue', modalId, 'queue'],
        data: remainingQueue,
      },
    });

    // Execute the action
    const { payload, isOpen, modalOption } = currentAction;
    const closeAfterSeconds = modalOption?.closeAfterSeconds;

    if (isOpen) {
      // Open the modal
      await dispatch(
        toggleModal({
          modalId,
          payload,
          isOpen: true,
          modalOption,
        })
      );

      // Wait for closeAfterSeconds
      if (closeAfterSeconds) {
        await new Promise(resolve =>
          setTimeout(resolve, closeAfterSeconds * 1000)
        );
      }

      // Check if queue was cleared during the wait
      const queueData = getOperationData(getState(), ['modalQueue', modalId]);
      if (!queueData) {
        return dispatch({ type: '' });
      }

      // Close the modal
      await dispatch(
        toggleModal({
          modalId,
          isOpen: false,
          modalOption,
        })
      );
    } else {
      // Close the modal
      await dispatch(
        toggleModal({
          modalId,
          isOpen: false,
          modalOption,
        })
      );
    }

    // Check if queue was cleared
    const queueDataAfter = getOperationData(getState(), [
      'modalQueue',
      modalId,
    ]);
    if (!queueDataAfter) {
      return dispatch({ type: '' });
    }

    // Set processing to false before processing next
    dispatch({
      type: SET_OPERATION_DATA,
      payload: {
        selectPath: ['modalQueue', modalId, 'isProcessing'],
        data: false,
      },
    });

    // Process next action in queue
    return dispatch(processModalQueue({ modalId }));
  };

/**
 * Queued toggle modal - use this instead of toggleModal for queued behavior
 * @kind action
 * @param {object} params - action params.
 * @param {string} params.modalId - modal id.
 * @param {object} params.payload - payload.
 * @param {boolean} params.isOpen - is open modal.
 * @param {object} params.modalOption - modal options.
 * @param {number} params.modalOption.closeAfterSeconds - Required. The modal will auto-close after this many seconds. Without this, the modal will open and immediately close.
 * @return {Promise} Action promise.
 */
export const queuedToggleModal =
  ({ modalId, payload, isOpen, modalOption } = {}) =>
  async dispatch => {
    if (!modalId) {
      return dispatch({ type: '' });
    }

    return dispatch(
      enqueueModalAction({
        modalId,
        payload,
        isOpen,
        modalOption,
      })
    );
  };

/**
 * Clear modal queue for a specific modal and close the modal
 * @kind action
 * @param {object} params - action params.
 * @param {string} params.modalId - modal id.
 * @return {Promise} Action promise.
 */
export const clearModalQueue =
  ({ modalId } = {}) =>
  async dispatch => {
    if (!modalId) {
      return dispatch({ type: '' });
    }
    dispatch({
      type: REMOVE_OPERATION_DATA,
      payload: {
        selectPath: ['modalQueue', modalId],
      },
    });
    return dispatch(toggleModal({ modalId, isOpen: false }));
  };

export default {
  queuedToggleModal,
  clearModalQueue,
};
